import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'

import { ENVELOPE_STATUS_FILTERS } from './api/v1/ecm/envelopes'
import { DefaultRoutePending } from './components/common/route-pending'
// Layouts (not lazy - always required)
import AppLayout from './layouts/app-layout'
import AuthLayout from './layouts/auth-layout'
import RootLayout from './layouts/root-layout'
import { APP_SCROLL_CONTAINER_SELECTOR } from './lib/app-scroll-container'
import {
  redirectIfAuthenticated,
  requireAuth,
  requireAuthOnly,
  requireAuthRemembering,
} from './lib/auth-guard'
import { blockWhiteLabelRegistration } from './lib/public-guards'
import { redirectToEnvelopesStatus } from './pages/envelope-list/envelopes.guard'

interface RouterContext {
  queryClient: QueryClient
}

// Root route
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: lazyRouteComponent(() => import('./pages/not-found')),
  errorComponent: lazyRouteComponent(() => import('./pages/error')),
})

// Auth layout route
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_auth',
  component: AuthLayout,
})

// Authenticated layout route
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authenticated',
  component: AppLayout,
  beforeLoad: requireAuth,
})

// Auth routes (public) - lazy loaded
const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/login',
  component: lazyRouteComponent(() => import('./pages/login')),
  beforeLoad: redirectIfAuthenticated,
})

const registerRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/register',
  component: lazyRouteComponent(() => import('./pages/register')),
  validateSearch: (search: Record<string, unknown>): { inviteId?: string } => ({
    inviteId: search.inviteId as string | undefined,
  }),
  beforeLoad: (context) => {
    redirectIfAuthenticated(context)
    blockWhiteLabelRegistration(context.search.inviteId)
  },
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/forgot-password',
  component: lazyRouteComponent(() => import('./pages/forgot-password')),
  beforeLoad: redirectIfAuthenticated,
})

const loginSsoRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/login/sso',
  beforeLoad: redirectIfAuthenticated,
  component: lazyRouteComponent(() => import('./pages/login-sso')),
})

// No redirectIfAuthenticated: a user re-authenticating via the IdP (e.g. a
// stale session) must be able to land here and complete the exchange rather
// than bounce straight back into the app on an already-consumed ticket.
const loginSsoCallbackRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/login/sso/callback',
  component: lazyRouteComponent(() => import('./pages/login-sso-callback')),
  validateSearch: (
    search: Record<string, unknown>,
  ): { ticket?: string; ssoError?: string } => ({
    ticket: (search.ticket as string) || undefined,
    ssoError: (search.ssoError as string) || undefined,
  }),
})

const loginMicrosoftCallbackRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/login/microsoft/callback',
  component: lazyRouteComponent(
    () => import('./pages/login-microsoft-callback'),
  ),
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    code?: string
    state?: string
    error?: string
    error_description?: string
  } => ({
    code: (search.code as string) || undefined,
    state: (search.state as string) || undefined,
    error: (search.error as string) || undefined,
    error_description: (search.error_description as string) || undefined,
  }),
})

const oauthConsentRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/oauth/consent',
  component: lazyRouteComponent(() => import('./pages/oauth-consent')),
  beforeLoad: requireAuthRemembering,
})

const switchRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/switch',
  component: lazyRouteComponent(() => import('./pages/switch')),
  beforeLoad: requireAuthOnly,
})

const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logout',
  component: lazyRouteComponent(() => import('./pages/logout')),
})

// Public routes (no auth required)
const publicSignMarkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-mark/$tempSignMarkId',
  component: lazyRouteComponent(() => import('./pages/sign-mark')),
  validateSearch: (search: Record<string, unknown>) => ({
    type: search.type,
    signatureLinkId: (search.signatureLinkId as string) || undefined,
  }),
})

const publicSelfieRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/selfie/$tempSelfieId',
  component: lazyRouteComponent(() => import('./pages/selfie')),
  validateSearch: (search: Record<string, unknown>) => ({
    signatureLinkId: (search.signatureLinkId as string) || undefined,
    mode: (search.mode as string) || undefined,
  }),
})

const publicIdentityDocumentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/identity-document/$tempIdentityDocumentId',
  component: lazyRouteComponent(() => import('./pages/identity-document')),
  validateSearch: (search: Record<string, unknown>) => ({
    signatureLinkId: (search.signatureLinkId as string) || undefined,
  }),
})

const publicReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/review/$signatureLinkId',
  component: lazyRouteComponent(() => import('./pages/envelope-review')),
})

const bulkReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/review/bulk/$sessionId',
  beforeLoad: requireAuth,
  component: lazyRouteComponent(
    () => import('./pages/envelope-review/bulk/bulk-review'),
  ),
})

const publicUnsubscribeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/unsubscribe/$signatureLinkId',
  component: lazyRouteComponent(() => import('./pages/unsubscribe')),
})

const publicEnvelopeViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/view/$envelopeId',
  component: lazyRouteComponent(() => import('./pages/envelope-public-view')),
})

const publicBadgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/badge/$id',
  component: lazyRouteComponent(() => import('./pages/badge')),
})

const publicBadgeGlobalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/badge',
  component: lazyRouteComponent(() => import('./pages/badge')),
})

const publicVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify',
  component: lazyRouteComponent(() => import('./pages/verify')),
})

// Authenticated routes - lazy loaded
const indexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/dashboard')),
  validateSearch: (search: Record<string, unknown>) => ({
    source: search.source as string | undefined,
  }),
})

const contactsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/contacts',
  component: lazyRouteComponent(() => import('./pages/contact-list')),
})

const envelopesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/envelopes',
  component: lazyRouteComponent(() => import('./pages/envelope-list')),
})

const envelopesIndexRoute = createRoute({
  getParentRoute: () => envelopesRoute,
  path: '/',
  beforeLoad: redirectToEnvelopesStatus,
})

const envelopesStatusRoutes = ENVELOPE_STATUS_FILTERS.map((status) =>
  createRoute({
    getParentRoute: () => envelopesRoute,
    path: status,
  }),
)

const envelopeDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/envelopes/$envelopeId',
  component: lazyRouteComponent(() => import('./pages/envelope-private-view')),
})

// Envelope creation/editing route
const envelopeNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/envelope',
  component: lazyRouteComponent(() => import('./pages/envelope-edit')),
  validateSearch: (search: Record<string, unknown>) => ({
    vaultId: (search.vaultId as string) || undefined,
    source: (search.source as string) || undefined,
    // Set by the template-detail "Use template" action so the envelope
    // page can offer the fulfillment flow for this template. Declared
    // here so the param survives validation.
    templateId: (search.templateId as string) || undefined,
  }),
})

const envelopeEditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/envelope/$envelopeId',
  component: lazyRouteComponent(() => import('./pages/envelope-edit')),
})

// Reports routes with layout
const reportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reports',
  component: lazyRouteComponent(() => import('./pages/reports/layout')),
})

const reportsIndexRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/reports/envelopes/overview', replace: true })
  },
})

const reportsEnvelopesOverviewRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/envelopes/overview',
  component: lazyRouteComponent(
    () => import('./pages/reports/envelopes/overview'),
  ),
})
const reportsEnvelopesPendingRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/envelopes/pending',
  component: lazyRouteComponent(
    () => import('./pages/reports/envelopes/pending'),
  ),
})
const reportsEnvelopesSealingRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/envelopes/sealing',
  component: lazyRouteComponent(
    () => import('./pages/reports/envelopes/sealing'),
  ),
})
const reportsEnvelopesBlockchainRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/envelopes/blockchain',
  component: lazyRouteComponent(
    () => import('./pages/reports/envelopes/blockchain'),
  ),
})
const reportsEnvelopesAccessesRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/envelopes/accesses',
  component: lazyRouteComponent(
    () => import('./pages/reports/envelopes/accesses'),
  ),
})

const reportsSignersActionsRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/signers/actions',
  component: lazyRouteComponent(
    () => import('./pages/reports/signers/actions'),
  ),
})
const reportsSignersIdentityRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/signers/identity',
  component: lazyRouteComponent(
    () => import('./pages/reports/signers/identity'),
  ),
})

const reportsSignersNotificationsRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/signers/notifications',
  component: lazyRouteComponent(
    () => import('./pages/reports/signers/notifications'),
  ),
})

const reportsTemplatesPerformanceRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/templates/performance',
  component: lazyRouteComponent(
    () => import('./pages/reports/templates/performance'),
  ),
})

const reportsTeamProductivityRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/team/productivity',
  component: lazyRouteComponent(
    () => import('./pages/reports/team/productivity'),
  ),
})

// Reports - Consumption
const reportsConsumptionStatementRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/consumption/statement',
  component: lazyRouteComponent(
    () => import('./pages/reports/consumption/statement'),
  ),
})
const reportsConsumptionBalanceRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/consumption/balance',
  component: lazyRouteComponent(
    () => import('./pages/reports/consumption/balance'),
  ),
})
const reportsConsumptionAllocationRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/consumption/allocation',
  component: lazyRouteComponent(
    () => import('./pages/reports/consumption/allocation'),
  ),
})
const reportsConsumptionStorageRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/consumption/storage',
  component: lazyRouteComponent(
    () => import('./pages/reports/consumption/storage'),
  ),
})

// Reports - System
const reportsSystemAuditRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/system/audit',
  component: lazyRouteComponent(() => import('./pages/reports/system/audit')),
})

const reportsSystemAccessRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/system/access',
  component: lazyRouteComponent(() => import('./pages/reports/system/access')),
})

const reportsSystemWebhooksRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/system/webhooks',
  component: lazyRouteComponent(
    () => import('./pages/reports/system/webhooks'),
  ),
})

const reportsLegacyEnvelopesRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/signatures/envelopes',
  beforeLoad: () => {
    throw redirect({ to: '/reports/envelopes/overview', replace: true })
  },
})
const reportsLegacySignerActionsRoute = createRoute({
  getParentRoute: () => reportsRoute,
  path: '/signatures/signer-actions',
  beforeLoad: () => {
    throw redirect({ to: '/reports/signers/actions', replace: true })
  },
})

// Affiliate program routes with layout — a product area of its own, mirroring
// the Reports tree: a layout that owns the submenu plus one route per page.
const affiliateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/affiliate',
  component: lazyRouteComponent(() => import('./pages/affiliate/layout')),
})

const affiliateIndexRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/affiliate/overview', replace: true })
  },
})

// Performance
const affiliateOverviewRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/overview',
  component: lazyRouteComponent(() => import('./pages/affiliate/overview')),
})

const affiliateAnalyticsRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/analytics',
  component: lazyRouteComponent(() => import('./pages/affiliate/analytics')),
})

const affiliateCustomersRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/customers',
  component: lazyRouteComponent(() => import('./pages/affiliate/customers')),
})

// Promotion
const affiliateLinksRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/links',
  component: lazyRouteComponent(() => import('./pages/affiliate/links')),
})

const affiliateCouponsRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/coupons',
  component: lazyRouteComponent(() => import('./pages/affiliate/coupons')),
})

const affiliateMaterialsRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/materials',
  component: lazyRouteComponent(() => import('./pages/affiliate/materials')),
})

// Finance
const affiliateCommissionsRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/commissions',
  component: lazyRouteComponent(() => import('./pages/affiliate/commissions')),
})

const affiliateWithdrawalsRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/withdrawals',
  component: lazyRouteComponent(() => import('./pages/affiliate/withdrawals')),
})

const affiliatePayoutMethodsRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/payout-methods',
  component: lazyRouteComponent(
    () => import('./pages/affiliate/payout-methods'),
  ),
})

// The two composite pages the program shipped with, before each of their cards
// became a page of its own. They point at the page that inherited the first
// card of the composite.
const affiliatePromotionRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/promotion',
  beforeLoad: () => {
    throw redirect({ to: '/affiliate/links', replace: true })
  },
})

const affiliateFinanceRoute = createRoute({
  getParentRoute: () => affiliateRoute,
  path: '/finance',
  beforeLoad: () => {
    throw redirect({ to: '/affiliate/commissions', replace: true })
  },
})

// Settings routes with layout
const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('./pages/settings/layout/layout')),
})

// Redirects while the route resolves rather than from a rendered component: a
// lazily loaded <Navigate> leaves the settings layout mounted with an empty
// outlet whenever its chunk or its effect does not land.
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/settings/profile/general', replace: true })
  },
})

// Profile settings routes
const settingsProfileGeneralRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/profile/general',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/profile/general'),
  ),
})

const settingsProfileNotificationsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/profile/notifications',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/profile/notifications'),
  ),
})

const settingsProfilePendingInvitesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/profile/pending-invites',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/profile/pending-invites'),
  ),
})

const settingsProfileSecurityRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/profile/security',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/profile/security'),
  ),
})

const settingsProfileSignaturesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/profile/signatures',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/profile/signatures'),
  ),
})

const settingsProfileDigitalCertificatesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/profile/digital-certificates',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/profile/digital-certificates'),
  ),
})

const settingsProfileApiTokensRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/profile/api-tokens',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/profile/api-tokens'),
  ),
})

const settingsProfileMcpRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/profile/mcp',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/profile/mcp'),
  ),
})

// Account settings routes
const settingsAccountGeneralRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/general',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/account/general'),
  ),
})

const settingsAccountNotificationsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/notifications',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/account/notifications'),
  ),
})

const settingsAccountManagedAccountsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/managed-accounts',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/account/managed-accounts'),
  ),
})

const settingsAccountSustainabilityBadgeRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/sustainability-badge',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/account/sustainability-badge'),
  ),
})

// The affiliate program used to be a single tabbed page under Account, then a
// settings section; it is a product area of its own now. Redirect while the
// route resolves, never from a rendered component (see the settings index
// route above).
const settingsAccountAffiliateProgramRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/affiliate-program',
  beforeLoad: () => {
    throw redirect({ to: '/affiliate/overview', replace: true })
  },
})

// White label moved out of Account into a section of its own; the old address
// is still in bookmarks and in e-mails we already sent.
const settingsAccountWhiteLabelRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/white-label',
  beforeLoad: () => {
    throw redirect({ to: '/settings/white-label/general', replace: true })
  },
})

const settingsWhiteLabelIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/white-label',
  beforeLoad: () => {
    throw redirect({ to: '/settings/white-label/general', replace: true })
  },
})

const settingsWhiteLabelGeneralRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/white-label/general',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/white-label/general'),
  ),
})

const settingsWhiteLabelLogosRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/white-label/logos',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/white-label/logos'),
  ),
})

const settingsWhiteLabelEmailRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/white-label/email',
  beforeLoad: () => {
    throw redirect({
      to: '/settings/white-label/messaging-channels',
      replace: true,
    })
  },
})

const settingsWhiteLabelMessagingChannelsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/white-label/messaging-channels',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/white-label/messaging-channels'),
  ),
})

const settingsWhiteLabelCaptchaRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/white-label/captcha',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/white-label/captcha'),
  ),
})

const settingsWhiteLabelDomainRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/white-label/domain',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/white-label/domain'),
  ),
})

const settingsWhiteLabelSealsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/white-label/seals',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/white-label/seals'),
  ),
})

const settingsAccountSsoRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/sso',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/account/sso'),
  ),
})

const settingsAccountAccessPolicyRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/access-policy',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/account/access-policy'),
  ),
})

const settingsAccountBlockchainRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/blockchain',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/account/blockchain'),
  ),
})

const settingsAccountAiAssistantRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/ai-assistant',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/account/ai-assistant'),
  ),
})

const settingsAccountWebhooksRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/webhooks',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/account/webhooks'),
  ),
})

const settingsAccountUsersRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/account/users',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/account/users'),
  ),
})

// Billing settings routes
const settingsBillingPlansRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/billing/plans',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/billing/plans'),
  ),
})

const settingsBillingInvoicesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/billing/invoices',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/billing/invoices'),
  ),
})

const settingsBillingTaxDataRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/billing/tax-data',
  component: lazyRouteComponent(
    () => import('./pages/settings/sections/billing/tax-data'),
  ),
})

// The four pages the program had while it lived under Settings. All four land
// on the program's own home — Overview — rather than on a namesake page: the
// Settings-era tabs carried a different layout and a different shape from the
// screens that inherited their names, so the home page is the honest landing
// for any of those bookmarks.
const settingsAffiliateAnalyticsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/affiliate/analytics',
  beforeLoad: () => {
    throw redirect({ to: '/affiliate/overview', replace: true })
  },
})

const settingsAffiliatePromotionRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/affiliate/promotion',
  beforeLoad: () => {
    throw redirect({ to: '/affiliate/overview', replace: true })
  },
})

const settingsAffiliateFinanceRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/affiliate/finance',
  beforeLoad: () => {
    throw redirect({ to: '/affiliate/overview', replace: true })
  },
})

const settingsAffiliateMaterialsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/affiliate/materials',
  beforeLoad: () => {
    throw redirect({ to: '/affiliate/overview', replace: true })
  },
})

const templatesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/templates',
  component: lazyRouteComponent(() => import('./pages/template-list')),
})

// Read-only template view — mirrors the envelope view route. The editor
// lives at `/template/$templateId`.
const templateDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/templates/$templateId',
  component: lazyRouteComponent(() => import('./pages/template-detail')),
})

// Template creation route — mirrors the envelope pattern (`/envelope`).
const templateNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/template',
  component: lazyRouteComponent(() => import('./pages/template-edit')),
  validateSearch: (search: Record<string, unknown>) => ({
    vaultId: (search.vaultId as string) || undefined,
  }),
})

// Template edit/workspace route — mirrors the envelope pattern
// (`/envelope/$templateId`). `?tab=` selects the active workspace tab.
const templateEditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/template/$templateId',
  component: lazyRouteComponent(() => import('./pages/template-edit')),
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search.tab === 'string' ? { tab: search.tab } : {},
})

const vaultsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/vaults',
  component: lazyRouteComponent(() => import('./pages/vault-list')),
})

const vaultDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/vaults/$vaultId',
  component: lazyRouteComponent(() => import('./pages/vault-detail')),
})

// Route tree
const routeTree = rootRoute.addChildren([
  authRoute.addChildren([
    loginRoute,
    loginSsoRoute,
    loginSsoCallbackRoute,
    loginMicrosoftCallbackRoute,
    registerRoute,
    forgotPasswordRoute,
    switchRoute,
    oauthConsentRoute,
  ]),
  authenticatedRoute.addChildren([
    indexRoute,
    contactsRoute,
    envelopesRoute.addChildren([envelopesIndexRoute, ...envelopesStatusRoutes]),
    envelopeDetailRoute,
    envelopeNewRoute,
    envelopeEditRoute,
    reportsRoute.addChildren([
      reportsIndexRoute,
      reportsEnvelopesOverviewRoute,
      reportsEnvelopesPendingRoute,
      reportsEnvelopesSealingRoute,
      reportsEnvelopesBlockchainRoute,
      reportsEnvelopesAccessesRoute,
      reportsSignersActionsRoute,
      reportsSignersIdentityRoute,
      reportsSignersNotificationsRoute,
      reportsTemplatesPerformanceRoute,
      reportsTeamProductivityRoute,
      reportsConsumptionStatementRoute,
      reportsConsumptionBalanceRoute,
      reportsConsumptionAllocationRoute,
      reportsConsumptionStorageRoute,
      reportsSystemAuditRoute,
      reportsSystemAccessRoute,
      reportsSystemWebhooksRoute,
      reportsLegacyEnvelopesRoute,
      reportsLegacySignerActionsRoute,
    ]),
    affiliateRoute.addChildren([
      affiliateIndexRoute,
      affiliateOverviewRoute,
      affiliateAnalyticsRoute,
      affiliateCustomersRoute,
      affiliateLinksRoute,
      affiliateCouponsRoute,
      affiliateMaterialsRoute,
      affiliateCommissionsRoute,
      affiliateWithdrawalsRoute,
      affiliatePayoutMethodsRoute,
      // Legacy
      affiliatePromotionRoute,
      affiliateFinanceRoute,
    ]),
    settingsRoute.addChildren([
      settingsIndexRoute,
      // Profile
      settingsProfileGeneralRoute,
      settingsProfileNotificationsRoute,
      settingsProfilePendingInvitesRoute,
      settingsProfileSecurityRoute,
      settingsProfileSignaturesRoute,
      settingsProfileDigitalCertificatesRoute,
      settingsProfileApiTokensRoute,
      settingsProfileMcpRoute,
      // Account
      settingsAccountGeneralRoute,
      settingsAccountNotificationsRoute,
      settingsAccountAccessPolicyRoute,
      settingsAccountManagedAccountsRoute,
      settingsAccountSustainabilityBadgeRoute,
      settingsAccountAffiliateProgramRoute,
      settingsAccountSsoRoute,
      settingsAccountBlockchainRoute,
      settingsAccountAiAssistantRoute,
      settingsAccountWebhooksRoute,
      settingsAccountUsersRoute,
      // White label
      settingsWhiteLabelIndexRoute,
      settingsWhiteLabelGeneralRoute,
      settingsWhiteLabelLogosRoute,
      settingsWhiteLabelEmailRoute,
      settingsWhiteLabelMessagingChannelsRoute,
      settingsWhiteLabelCaptchaRoute,
      settingsWhiteLabelDomainRoute,
      settingsWhiteLabelSealsRoute,
      // Billing
      settingsBillingPlansRoute,
      settingsBillingInvoicesRoute,
      settingsBillingTaxDataRoute,
      // Legacy redirects
      settingsAccountWhiteLabelRoute,
      // Affiliate program — legacy redirects
      settingsAffiliateAnalyticsRoute,
      settingsAffiliatePromotionRoute,
      settingsAffiliateFinanceRoute,
      settingsAffiliateMaterialsRoute,
    ]),
    templatesRoute,
    templateDetailRoute,
    templateEditRoute,
    templateNewRoute,
    vaultsRoute,
    vaultDetailRoute,
  ]),
  logoutRoute,
  publicSignMarkRoute,
  publicSelfieRoute,
  publicIdentityDocumentRoute,
  publicReviewRoute,
  bulkReviewRoute,
  publicUnsubscribeRoute,
  publicEnvelopeViewRoute,
  publicBadgeRoute,
  publicBadgeGlobalRoute,
  publicVerifyRoute,
])

export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: 'viewport',
    scrollRestoration: true,
    // The window never scrolls here: SidebarInset is the scrollable element,
    // so scroll-to-top on navigation has to be pointed at it explicitly.
    scrollToTopSelectors: [APP_SCROLL_CONTAINER_SELECTOR],
    defaultStructuralSharing: true,
    defaultPreloadStaleTime: 0,
    defaultPendingComponent: DefaultRoutePending,
    defaultPendingMs: 1_000,
    defaultPendingMinMs: 100,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
